<template>
  <div>
    <Widget>
      <WidgetHeader
        >{{ data ? 'Edit Existing' : 'Create New' }} RTB</WidgetHeader
      >
      <WidgetBody>
        <form id="upsert-campaign-rtb" @submit.prevent="saveForm" ref="form">
          <div class="grid gap-2">
            <YInput v-model="rtbCampaign.label" label="Label" />
            <YInput v-model="rtbCampaign.bid" label="Bid" :required="true" />
            <YInput
              v-model="rtbCampaign.destinationUrl"
              label="Destination Url"
              :required="true"
            />
            <div class="flex gap-2 w-full">
              <YInput
                v-model="rtbCampaign.capNumber"
                label="Cap Number"
                type="number"
                class="w-1/2"
              />
              <Superselect
                title="Cap Frequency"
                v-model="rtbCampaign.capFrequency"
                :multiple="false"
                :options="['total', 'daily']"
                class="w-1/2"
              ></Superselect>
            </div>
            <Superselect
              title="Country"
              v-model="rtbCampaign.filters.countryCodes.value"
              :options="countryOptions"
              :modifiers="rtbCampaign.filters.countryCodes.modifiers"
              class="flex-grow"
              label="label"
              track-by="code"
              :multiple="true"
            ></Superselect>
            <Superselect
              title="Domains"
              v-model="rtbCampaign.filters.domains.value"
              :multiple="true"
              :modifiers="rtbCampaign.filters.domains.modifiers"
              track-by="id"
              label="label"
              :options="[]"
              editPlaceholder="Enter Values"
            />
            <Superselect
              title="Referers"
              v-model="rtbCampaign.filters.referers.value"
              :multiple="true"
              :modifiers="rtbCampaign.filters.referers.modifiers"
              track-by="id"
              label="label"
              :options="[]"
              editPlaceholder="Enter Values"
            />
            <Superselect
              title="IPs"
              v-model="rtbCampaign.filters.ips.value"
              :multiple="true"
              :modifiers="rtbCampaign.filters.ips.modifiers"
              track-by="id"
              label="label"
              :options="[]"
              editPlaceholder="Enter Values"
            />
            <Superselect
              title="Keywords"
              v-model="rtbCampaign.filters.keywords.value"
              :multiple="true"
              :modifiers="rtbCampaign.filters.keywords.modifiers"
              track-by="id"
              label="label"
              :options="[]"
              editPlaceholder="Enter Values"
            />
            <Superselect
              title="SubIds"
              v-model="rtbCampaign.filters.subIds.value"
              :multiple="true"
              :modifiers="rtbCampaign.filters.subIds.modifiers"
              track-by="id"
              label="label"
              :options="[]"
              editPlaceholder="Enter Values"
            />
            <Superselect
              title="Reserved Urls"
              v-model="rtbCampaign.filters.reservedUrls.value"
              :multiple="true"
              :modifiers="rtbCampaign.filters.reservedUrls.modifiers"
              track-by="id"
              label="label"
              :options="[]"
              editPlaceholder="Enter Values"
            />

            <div class="w-full flex justify-end gap-3">
              <YButton color="gray" type="button" @click="$emit('close')"
                >Cancel</YButton
              >
              <YButton color="blue" type="submit" :isLoading="isSaving"
                >Save</YButton
              >
            </div>
          </div>
        </form>
      </WidgetBody>
    </Widget>
  </div>
</template>

<script>
import COUNTRIES_QUERY from '@/graphql/Country/CountriesQuery.gql'
import UPSERT_CAMPAIGN_RTB from '@/graphql/Campaign/UpsertCampaignRtb.gql'

export default {
  props: {
    data: {
      type: Object
    }
  },
  apollo: {
    countries: {
      query: COUNTRIES_QUERY,
      variables() {
        return {
          orderBy: [
            {
              column: 'name',
              order: 'ASC'
            }
          ]
        }
      },
      result({ data }) {
        this.countryOptions = data.countries.filter(country => {
          const exclude = ['?', 'A1', 'A2']
          return !exclude.includes(country.code)
        })
      }
    }
  },
  data() {
    return {
      isSaving: false,
      countryOptions: [],
      rtbCampaign: {
        label: null,
        bid: null,
        destinationUrl: null,
        capFrequency: null,
        capNumber: null,
        filters: {
          countryCodes: {
            value: [],
            modifiers: { exclude: false }
          },
          domains: {
            value: null,
            modifiers: { exclude: false, edit: true }
          },
          referers: {
            value: null,
            modifiers: { exclude: false, edit: true }
          },
          ips: {
            value: null,
            modifiers: { exclude: false, edit: true }
          },
          keywords: {
            value: null,
            modifiers: { exclude: false, edit: true }
          },
          subIds: {
            value: null,
            modifiers: { exclude: false, edit: true }
          },
          reservedUrls: {
            value: null,
            modifiers: { exclude: false, edit: true }
          }
        }
      }
    }
  },
  created() {
    if (this.data) {
      this.populateFormFields(this.data)
    }
  },
  methods: {
    saveForm() {
      this.isSaving = true
      this.$apollo
        .mutate({
          mutation: UPSERT_CAMPAIGN_RTB,
          variables: {
            input: {
              id: this.data?.id ?? undefined,
              bid: parseFloat(this.rtbCampaign.bid),
              destinationUrl: this.rtbCampaign.destinationUrl,
              label: this.rtbCampaign.label,
              capFrequency: this.rtbCampaign.capFrequency,
              capNumber: parseInt(this.rtbCampaign.capNumber),
              filters: {
                countryCodes:
                  this.rtbCampaign.filters.countryCodes.value.length > 0
                    ? this.transformCountryCodes(
                        this.rtbCampaign.filters.countryCodes.value
                      )
                    : undefined,
                domains: this.rtbCampaign.filters.domains.value
                  ? this.transformFilterValues(this.rtbCampaign.filters.domains)
                  : undefined,
                referers: this.rtbCampaign.filters.referers.value
                  ? this.transformFilterValues(
                      this.rtbCampaign.filters.referers
                    )
                  : undefined,
                ips: this.rtbCampaign.filters.ips.value
                  ? this.transformFilterValues(this.rtbCampaign.filters.ips)
                  : undefined,
                keywords: this.rtbCampaign.filters.keywords.value
                  ? this.transformFilterValues(
                      this.rtbCampaign.filters.keywords
                    )
                  : undefined,
                subIds: this.rtbCampaign.filters.subIds.value
                  ? this.transformFilterValues(this.rtbCampaign.filters.subIds)
                  : undefined,
                reservedUrls: this.rtbCampaign.filters.reservedUrls.value
                  ? this.transformFilterValues(
                      this.rtbCampaign.filters.reservedUrls
                    )
                  : undefined
              }
            }
          }
        })
        .then(() => {
          this.isSaving = false
          this.$emit('close')
          this.$events.fire('refetchCampaignRtbs')
          this.$toastr.s('RTB Created', 'Success!')
        })
        .catch(error => {
          this.$toastr.e(error, 'Error')
        })
    },
    transformFilterValues(input) {
      if (!input || !Array.isArray(input.value) || input.value.length === 0)
        return []

      const exclude = input.modifiers?.exclude === true
      return input.value.map(item => {
        const val = item.id || item.label
        return exclude ? `!${val}` : val
      })
    },
    transformCountryCodes(countries) {
      const exclude =
        this.rtbCampaign.filters.countryCodes.modifiers.exclude === true

      return countries
        .map(country => country.code)
        .map(code => (exclude ? `!${code}` : code))
    },
    // incoming data, transform to populate the form
    populateFormFields(data) {
      this.rtbCampaign.label = data.label || null
      this.rtbCampaign.bid = data.bid || null
      this.rtbCampaign.destinationUrl = data.destinationUrl || null
      this.rtbCampaign.capFrequency = data.capFrequency || null
      this.rtbCampaign.capNumber = data.capNumber || null

      const filters = data.filters || {}

      const transform = (input, hasEdit = true) => {
        if (!input || !Array.isArray(input)) {
          return {
            value: null,
            modifiers: { exclude: false, ...(hasEdit ? { edit: true } : {}) }
          }
        }

        const exclude = input.every(
          val => typeof val === 'string' && val.startsWith('!')
        )
        const value = input.map(val => {
          const clean = val.replace(/^!/, '')
          return { id: clean, label: clean }
        })

        return {
          value,
          modifiers: { exclude, ...(hasEdit ? { edit: true } : {}) }
        }
      }

      this.rtbCampaign.filters = {
        countryCodes: {
          value: (filters.countryCodes || []).map(code => {
            const cleaned = code.replace(/^!/, '')
            const country = this.countryOptions.find(c => c.code === cleaned)
            return country || { code: cleaned, label: cleaned }
          }),
          modifiers: {
            exclude:
              Array.isArray(filters.countryCodes) &&
              filters.countryCodes.every(code => code.startsWith('!'))
          }
        },
        domains: transform(filters.domains),
        referers: transform(filters.referers),
        ips: transform(filters.ips),
        keywords: transform(filters.keywords),
        subIds: transform(filters.subIds),
        reservedUrls: transform(filters.reservedUrls)
      }
    }
  }
}
</script>
